import styled from 'styled-components';

import { ActionStatus, WorkflowStatus } from 'models/Workflow';

// One status maps to exactly one presentation everywhere it appears (UX guide §5).
export type Tone = 'neutral' | 'info' | 'warning' | 'success' | 'danger';

export const TONE_STYLES: Record<Tone, { background: string; color: string }> = {
  neutral: { background: 'rgba(15,18,38,0.06)', color: 'rgba(15,18,38,0.6)' },
  info: { background: 'rgba(50,83,239,0.1)', color: '#3253ef' },
  warning: { background: 'rgba(245,130,94,0.16)', color: '#c0552f' },
  success: { background: 'rgba(46,163,16,0.1)', color: '#2EA310' },
  danger: { background: 'rgba(209,13,68,0.1)', color: '#DC2853' },
};

const WORKFLOW_STATUS_DISPLAY: Record<WorkflowStatus, { label: string; tone: Tone }> = {
  [WorkflowStatus.RUNNING]: { label: 'In Progress', tone: 'info' },
  [WorkflowStatus.ACTION_REQUIRED]: { label: 'Needs Attention', tone: 'warning' },
  [WorkflowStatus.COMPLETED]: { label: 'Completed', tone: 'success' },
  [WorkflowStatus.CANCELED]: { label: 'Canceled', tone: 'neutral' },
};

const ACTION_STATUS_DISPLAY: Record<ActionStatus, { label: string; tone: Tone }> = {
  [ActionStatus.PENDING]: { label: 'Pending', tone: 'neutral' },
  [ActionStatus.RUNNING]: { label: 'Running', tone: 'info' },
  [ActionStatus.ACTION_REQUIRED]: { label: 'Action Needed', tone: 'warning' },
  [ActionStatus.FLAGGED]: { label: 'Needs Info', tone: 'warning' },
  [ActionStatus.COMPLETED]: { label: 'Completed', tone: 'success' },
  [ActionStatus.ERRORED]: { label: 'Error', tone: 'danger' },
  [ActionStatus.CANCELED]: { label: 'Canceled', tone: 'neutral' },
  [ActionStatus.SKIPPED]: { label: 'Not Applicable', tone: 'neutral' },
};

export function workflowStatusDisplay(status: WorkflowStatus) {
  return WORKFLOW_STATUS_DISPLAY[status] ?? { label: status, tone: 'neutral' as Tone };
}

export function actionStatusDisplay(status: ActionStatus) {
  return ACTION_STATUS_DISPLAY[status] ?? { label: status, tone: 'neutral' as Tone };
}

export function WorkflowStatusBadge({ status }: { status: WorkflowStatus }) {
  const display = workflowStatusDisplay(status);
  return (
    <Pill $tone={display.tone} data-testid='workflow-status'>
      {display.label}
    </Pill>
  );
}

export function ActionStatusBadge({ status }: { status: ActionStatus }) {
  const display = actionStatusDisplay(status);
  return (
    <Pill $tone={display.tone} data-testid='action-status'>
      {display.label}
    </Pill>
  );
}

const Pill = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  border-radius: 24px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background-color: ${({ $tone }) => TONE_STYLES[$tone].background};
  color: ${({ $tone }) => TONE_STYLES[$tone].color};
`;
